import { ComponentType, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { t } from 'i18n-js';

import { fetchConfigurationIfNeeded } from '@lootboy/lib/actions/ConfigurationActions';
import {
  EARN_COINS_HINT_KEYS_LIST,
  EARN_DIAMONDS_HINT_KEYS_LIST,
} from '@lootboy/lib/constants';
import useFetchSelector from '@lootboy/lib/hooks/useFetchSelector';

import { Divider } from '~/components/atoms/Divider';
import { ListContainer } from '~/components/atoms/ListContainer';
import { ScrollContainer } from '~/components/atoms/ScrollContainer';
import StyledButton, {
  EStyledButtonVariant,
} from '~/components/atoms/StyledButton';
import LoadingWrapper from '~/components/LoadingWrapper';
import BottomSheetDialog from '~/components/molecules/BottomSheetDialog';

import { useDocumentTitle } from '~/hooks/useDocumentTitle';
import useOnClose from '~/hooks/useOnClose';
import { IConfiguration } from '~/interfaces/IConfig';
import { Either } from '~/interfaces/types';
import { configurationPlainSelector } from '~/redux/selectors';
import {
  headlineOfferWallTranslation,
  subHeadlineOfferWallTranslation,
} from '~/utilities/offerWall';
import { useNumberParser } from '~/utils';

import HintTile, { THintTile } from './HintTile';

import styles from './BalancePopup.module.scss';

export type THintTileExtended = THintTile &
  Either<
    {
      component: ComponentType<{ children: unknown }>;
    },
    { to: string }
  >;

export type THint = Omit<THintTileExtended, 'headline' | 'subheadline'> &
  Either<{ offerWallName: string }, { name: string }>;

export interface IBalancePopup {
  translation: string;
  src: string;
  coins?: number;
  hints: {
    [
      key:
        | keyof typeof EARN_DIAMONDS_HINT_KEYS_LIST
        | keyof typeof EARN_COINS_HINT_KEYS_LIST
    ]: THint;
  };
  configKey: keyof IConfiguration;
}

export default function BalancePopup({
  translation,
  src,
  coins = 0,
  hints,
  configKey,
}: IBalancePopup): JSX.Element {
  const onClose = useOnClose();
  const getParsedNumber = useNumberParser();
  const {
    data,
    lastSuccessfulFetch,
  }: { data: IConfiguration; lastSuccessfulFetch: number } = useFetchSelector(
    fetchConfigurationIfNeeded,
    // @ts-ignore proper lib typing need to be done
    configurationPlainSelector,
  );

  useDocumentTitle(t(`${translation}_headline`));

  const hintsFiltered = useMemo(
    () =>
      (data[configKey] || [])
        .filter(({ enabled, key }) => enabled && hints[key])
        .map(({ key }) => hints[key]),
    [data, hints, configKey],
  );

  return (
    <BottomSheetDialog
      className={styles.container}
      onClose={onClose}
      header={t(`${translation}_bc`)}
      contentClassName={styles.scrollContainer}
      renderFooter={() => (
        <StyledButton
          variant={EStyledButtonVariant.BLUE}
          className={styles.okButton}
          onClick={onClose}
        >
          {t('common_okay')}
        </StyledButton>
      )}
    >
      <ScrollContainer>
        <div className={styles.text}>
          <div className={styles.balanceWrapper}>
            <img
              src={src}
              alt="currency"
              className={styles.currencyIcon}
            />
            <div className={styles.balanceValueWrapper}>
              <div className={styles.balanceDescription}>
                {t('common_your_balance')}
              </div>
              <div className={styles.balance}>
                {`${getParsedNumber(coins)} ${t(`${translation}_bc`)}`}
              </div>
            </div>
          </div>
        </div>
        <Divider className={styles.divider} />
        <div className={styles.secondHeader}>
          {t(`${translation}_headline`)}
        </div>
        <LoadingWrapper loading={!lastSuccessfulFetch}>
          <ListContainer className={styles.list}>
            {hintsFiltered.map(
              ({
                name,
                offerWallName,
                img,
                to,
                highlight,
                component: Component,
              }) => {
                const hint = (
                  <HintTile
                    img={img}
                    highlight={highlight}
                    headline={
                      offerWallName
                        ? headlineOfferWallTranslation(offerWallName)
                        : t(`${translation}_${name}_bpoint`)
                    }
                    subheadline={
                      offerWallName
                        ? subHeadlineOfferWallTranslation(offerWallName)
                        : t(`${translation}_${name}`)
                    }
                  />
                );

                return (
                  <li key={name || offerWallName}>
                    {typeof Component === 'function' ? (
                      <Component>{hint}</Component>
                    ) : (
                      <Link to={to ?? ''}>{hint}</Link>
                    )}
                  </li>
                );
              },
            )}
          </ListContainer>
        </LoadingWrapper>
      </ScrollContainer>
    </BottomSheetDialog>
  );
}
